import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN = '#1b4332';
const AMBER = '#b45309';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${AMBER} 0%, #d97706 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 0.8,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const CELL_SX = { py: 0.9, px: 0.8, fontSize: '0.77rem', textAlign: 'center' };

function fmtNum(v, d = 3) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isGrandTotal(row) {
  return String(row.FOREST || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Generic summary table ────────────────────────────────────────────────────
function SummaryTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 500 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#1a3a2a' : (i % 2 === 0 ? '#fff' : '#f5fbf7'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#ffffff' : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Raw detail table (RS[3]) ─────────────────────────────────────────────────
function RawDetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  const cols = [
    { label: '#',             key: '__idx' },
    { label: 'FOREST',        key: 'FOREST' },
    { label: 'ESSENCE',       key: 'ESSENCE' },
    { label: 'QUALITY',       key: 'QUALITY' },
    { label: 'LOG NO',        key: 'LOG_NO' },
    { label: 'CHALAN NO',     key: 'CHALAN_NO' },
    { label: 'CHALAN DATE',   key: 'CHALAN_DATE',    fmt: v => v ? String(v).substring(0, 10) : '—' },
    { label: 'RECV. DATE',    key: 'RECEIVING_DATE', fmt: v => v ? String(v).substring(0, 10) : '—' },
    { label: 'CBM (REF.)',    key: 'BORD_ACTUAL_CBM',fmt: v => fmtNum(v, 3) },
    { label: 'CBM (BORD.)',   key: 'CBM_BORDEREAU',  fmt: v => fmtNum(v, 3) },
    { label: 'CBM (CALC.)',   key: 'CBM_CALCULATION',fmt: v => fmtNum(v, 3) },
    { label: 'COMPANY',       key: 'COMPANY' },
    { label: 'ORIGIN',        key: 'ORIGIN' },
  ];
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f5fbf7',
              '&:hover': { filter: 'brightness(0.97)' },
            }}>
              {cols.map(col => {
                const raw = col.key === '__idx' ? i + 1 : row[col.key];
                const val = col.fmt ? col.fmt(raw) : (raw ?? '—');
                return <TableCell key={col.label} sx={CELL_SX}>{val}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Column definitions ───────────────────────────────────────────────────────
const COLS_QUALITY = [
  { label: 'FOREST',          key: 'FOREST' },
  { label: 'ESSENCE',         key: 'ESSENCE' },
  { label: 'QUALITY',         key: 'QUALITY' },
  { label: 'LOG COUNT',       key: 'LOG_COUNT',       fmt: v => fmtNum(v, 0) },
  { label: 'CBM (REF.)',      key: 'BORD_ACTUAL_CBM', fmt: v => fmtNum(v, 3) },
  { label: 'CBM (BORDEREAU)', key: 'CBM_BORDEREAU',   fmt: v => fmtNum(v, 3) },
  { label: 'CBM (CALC.)',     key: 'CBM_CALCULATION', fmt: v => fmtNum(v, 3) },
];

const COLS_ESSENCE = [
  { label: 'FOREST',          key: 'FOREST' },
  { label: 'ESSENCE',         key: 'ESSENCE' },
  { label: 'LOG COUNT',       key: 'LOG_COUNT',       fmt: v => fmtNum(v, 0) },
  { label: 'CBM (REF.)',      key: 'BORD_ACTUAL_CBM', fmt: v => fmtNum(v, 3) },
  { label: 'CBM (BORDEREAU)', key: 'CBM_BORDEREAU',   fmt: v => fmtNum(v, 3) },
  { label: 'CBM (CALC.)',     key: 'CBM_CALCULATION', fmt: v => fmtNum(v, 3) },
];

const COLS_FOREST = [
  { label: 'FOREST',          key: 'FOREST' },
  { label: 'LOG COUNT',       key: 'LOG_COUNT',       fmt: v => fmtNum(v, 0) },
  { label: 'CBM (REF.)',      key: 'BORD_ACTUAL_CBM', fmt: v => fmtNum(v, 3) },
  { label: 'CBM (BORDEREAU)', key: 'CBM_BORDEREAU',   fmt: v => fmtNum(v, 3) },
  { label: 'CBM (CALC.)',     key: 'CBM_CALCULATION', fmt: v => fmtNum(v, 3) },
];

const TABS = [
  { label: 'BY FOREST + ESSENCE' },
  { label: 'BY FOREST + QUALITY' },
  { label: 'BY FOREST ONLY' },
  { label: 'RAW DETAIL' },
];

// ─── Main view ────────────────────────────────────────────────────────────────
const LogClosingStockView = ({ onBackClick, user }) => {
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [data,     setData]     = useState(null);
  const [asOnDate, setAsOnDate] = useState(null);
  const [tab,      setTab]      = useState(0);

  const fetchData = async () => {
    if (!asOnDate) { setError('Please select As On Date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          procedure: 'proc_rp_logstocknotcutted3',
          parameters: { dateofstk: asOnDate.format('YYYY-MM-DD') },
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    [
      { label: 'By Forest+Quality',  rows: data[0] },
      { label: 'By Forest+Essence',  rows: data[1] },
      { label: 'By Forest Only',     rows: data[2] },
      { label: 'Raw Detail',         rows: data[3] },
    ].forEach(({ label, rows }) => {
      if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
    });
    XLSX.writeFile(wb, `LogClosingStock_${asOnDate.format('YYYY-MM-DD')}.xlsx`);
  };

  // Grand total from RS[1] (Forest+Essence)
  const grandTotal = data?.[1]?.find(r => isGrandTotal(r));

  const renderTable = () => {
    if (!data) return null;
    switch (tab) {
      case 0: return <SummaryTable rows={data[1]} cols={COLS_ESSENCE} />;
      case 1: return <SummaryTable rows={data[0]} cols={COLS_QUALITY} />;
      case 2: return <SummaryTable rows={data[2]} cols={COLS_FOREST}  />;
      case 3: return <RawDetailTable rows={data[3]} />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(180,83,9,0.1)', border: '1px solid #fef3c7' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: 'linear-gradient(135deg, #1a3a2a 0%, #2d5a3d 100%)', borderRadius: 2, p: 1.5, mr: 2 }}>
              <InventoryIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Log Closing Stock — As On Date</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Log inventory status as on a selected date</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="As On Date"
                value={asOnDate}
                onChange={setAsOnDate}
                maxDate={dayjs()}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: GREEN,
                  '&:hover': { backgroundColor: '#0f2419' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Quick stats */}
          {grandTotal && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
              {[
                { label: 'Total Logs',    val: fmtNum(grandTotal.LOG_COUNT, 0),       color: GREEN },
                { label: 'CBM (Ref.)',    val: fmtNum(grandTotal.BORD_ACTUAL_CBM, 3), color: AMBER },
                { label: 'CBM (Bord.)',   val: fmtNum(grandTotal.CBM_BORDEREAU, 3),   color: AMBER },
                { label: 'CBM (Calc.)',   val: fmtNum(grandTotal.CBM_CALCULATION, 3), color: AMBER },
              ].map(s => (
                <Box key={s.label} sx={{
                  px: 2, py: 1.5, borderRadius: 2, textAlign: 'center', minWidth: 120,
                  border: `1.5px solid ${s.color}30`, background: `${s.color}08`,
                }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: s.color }}>{s.val}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #fef3c7' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: AMBER },
                '& .MuiTabs-indicator': { backgroundColor: AMBER },
              }}
            >
              {TABS.map((t, i) => <Tab key={i} label={t.label} />)}
            </Tabs>
            <Button
              size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {renderTable()}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Log Closing Stock to WhatsApp"
            apiUrl={
              asOnDate
                ? `${API_URL}/api/logstock/send-closing?date=${asOnDate.format('YYYY-MM-DD')}`
                : null
            }
            disabled={!asOnDate}
          />
        </Box>
      )}
    </Container>
  );
};

export default LogClosingStockView;
